import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Lock, ArrowLeft, Calendar, Loader2 } from 'lucide-react';
import { InlineWidget } from 'react-calendly';

const PRODUCTS = {
  book: {
    name: "Story Framework Formula",
    price: 17,
    description: "The complete framework for transforming ideas into franchise-ready IP",
    features: [
      "The 5-part framework that transforms ideas into franchise-ready IP",
      "Templates for world-building, character arcs, and expansion mapping",
      "Case studies from successful franchise launches",
      "Instant PDF download access"
    ],
    orderBump: {
      name: "Course Upgrade",
      description: "Add the full AI for Creatives Course at a 50% discount",
      price: 97,
      originalPrice: 197
    }
  },
  course: {
    name: "AI for Creatives Course",
    standardPrice: 197,
    vipPrice: 497,
    description: "Complete system for building franchise-ready IP with AI workflows",
    standardFeatures: [
      "Complete AI production workflow from concept to pitch materials",
      "Character consistency frameworks for visual IP development",
      "Tool-agnostic systems that work as AI evolves",
      "Private community access",
      "Lifetime updates"
    ],
    vipFeatures: [
      "Everything in Standard, plus:",
      "30-minute Strategy Audit call with Ramon",
      "Priority support and feedback",
      "Early access to new modules"
    ]
  },
  audit: {
    name: "60 Minute IP Strategy Audit",
    price: 497,
    description: "Deep-dive analysis of your IP's franchise potential with custom roadmap",
    features: [
      "90-minute deep-dive analysis of your IP's franchise potential",
      "Identify highest-probability monetization paths",
      "Custom roadmap based on current market conditions",
      "Specific intro recommendations where relevant",
      "Recording of the session for future reference"
    ],
    calendlyUrl: "https://calendly.com/ramon-govea/60-minute-ip-strategy-audit"
  }
};

const CheckoutPage = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tier = searchParams.get('tier') || 'standard';
  
  const [email, setEmail] = useState('');
  const [orderBump, setOrderBump] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Get product details
  const product = PRODUCTS[productId];
  
  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Product not found</h1>
          <button
            onClick={() => navigate('/ai-for-creatives')}
            className="text-[#c9a962] underline"
          >
            Return to quiz
          </button>
        </div>
      </div>
    );
  }

  const getPrice = () => {
    if (productId === 'course') {
      return tier === 'vip' ? product.vipPrice : product.standardPrice;
    }
    let price = product.price;
    if (orderBump && product.orderBump) {
      price += product.orderBump.price;
    }
    return price;
  };

  const handleCheckout = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const actualProductId = productId === 'course' 
        ? (tier === 'vip' ? 'course_vip' : 'course_standard')
        : productId;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: actualProductId,
          email,
          origin_url: window.location.origin,
          order_bump: orderBump
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to process checkout. Please try again.');
      setIsLoading(false);
    }
  };

  // Special handling for audit - show Calendly
  if (productId === 'audit') {
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
              Back to results
            </button>
            <h1 className="text-3xl md:text-4xl font-serif mb-2">{product.name}</h1>
            <p className="text-gray-400">{product.description}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Details */}
            <div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-8 mb-8">
                <p className="text-3xl font-bold text-[#c9a962] mb-6">${product.price}</p>
                <ul className="space-y-4">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check size={20} className="text-[#c9a962] mt-0.5 shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-[#c9a962]/10 to-transparent border border-[#c9a962]/30 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar size={20} className="text-[#c9a962]" />
                  <h3 className="font-semibold">How It Works</h3>
                </div>
                <ol className="space-y-2 text-sm text-gray-300">
                  <li>1. Select a time that works for you</li>
                  <li>2. Complete payment to confirm your booking</li>
                  <li>3. Receive calendar invite and prep materials</li>
                  <li>4. Join the call and get your custom roadmap</li>
                </ol>
              </div>
            </div>

            {/* Calendly Widget */}
            <div className="bg-white rounded-xl overflow-hidden">
              <InlineWidget
                url={product.calendlyUrl}
                styles={{ height: '700px', minWidth: '320px' }}
                pageSettings={{
                  backgroundColor: 'ffffff',
                  hideEventTypeDetails: false,
                  hideLandingPageDetails: false,
                  primaryColor: 'c9a962',
                  textColor: '1a1a1a'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular checkout for book and course
  return (
    <div className="min-h-screen bg-[#0a0a12] text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Back to results
          </button>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Product Info - Left Column */}
          <div className="lg:col-span-3">
            <h1 className="text-3xl font-serif mb-2">
              {productId === 'course' && tier === 'vip' ? `${product.name} (VIP)` : product.name}
            </h1>
            <p className="text-gray-400 mb-8">{product.description}</p>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
              <h3 className="font-semibold mb-4">What's Included:</h3>
              <ul className="space-y-3">
                {(productId === 'course' 
                  ? (tier === 'vip' ? product.vipFeatures : product.standardFeatures)
                  : product.features
                ).map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={18} className="text-[#c9a962] mt-0.5 shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Order Bump for Book */}
            {productId === 'book' && product.orderBump && (
              <div 
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                  orderBump 
                    ? 'border-[#c9a962] bg-[#c9a962]/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
                onClick={() => setOrderBump(!orderBump)}
                data-testid="order-bump"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center shrink-0 ${
                    orderBump ? 'border-[#c9a962] bg-[#c9a962]' : 'border-white/30'
                  }`}>
                    {orderBump && <Check size={14} className="text-black" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">YES! Add {product.orderBump.name}</span>
                      <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded">50% OFF</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{product.orderBump.description}</p>
                    <p className="text-sm">
                      <span className="text-gray-500 line-through">${product.orderBump.originalPrice}</span>
                      <span className="text-[#c9a962] font-bold ml-2">${product.orderBump.price}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Course Tier Toggle */}
            {productId === 'course' && (
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => navigate(`/ai-for-creatives/checkout/course?tier=standard`)}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                    tier === 'standard' 
                      ? 'bg-[#c9a962] text-black' 
                      : 'border border-white/20 text-white hover:border-white/40'
                  }`}
                  data-testid="tier-standard"
                >
                  Standard — ${product.standardPrice}
                </button>
                <button
                  onClick={() => navigate(`/ai-for-creatives/checkout/course?tier=vip`)}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                    tier === 'vip' 
                      ? 'bg-[#c9a962] text-black' 
                      : 'border border-white/20 text-white hover:border-white/40'
                  }`}
                  data-testid="tier-vip"
                >
                  VIP — ${product.vipPrice}
                </button>
              </div>
            )}
          </div>

          {/* Checkout Form - Right Column */}
          <div className="lg:col-span-2">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 sticky top-6">
              <h3 className="text-xl font-semibold mb-6">Complete Your Order</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full p-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-[#c9a962]"
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              {/* Order Summary */}
              <div className="border-t border-white/10 pt-4 mb-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>{productId === 'course' && tier === 'vip' ? `${product.name} (VIP)` : product.name}</span>
                  <span>${productId === 'course' ? (tier === 'vip' ? product.vipPrice : product.standardPrice) : product.price}</span>
                </div>
                {orderBump && product.orderBump && (
                  <div className="flex justify-between text-sm text-gray-400 mb-2">
                    <span>{product.orderBump.name}</span>
                    <span>${product.orderBump.price}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10 mt-2">
                  <span>Total</span>
                  <span className="text-[#c9a962]">${getPrice()}</span>
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-sm mb-4" data-testid="checkout-error">{error}</p>
              )}

              <button
                onClick={handleCheckout}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-[#c9a962] to-[#d4b978] text-black py-4 font-semibold rounded-lg disabled:opacity-70 transition-all hover:shadow-lg hover:shadow-[#c9a962]/30 flex items-center justify-center gap-2"
                data-testid="checkout-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Complete Purchase
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Secure checkout powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
